import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { BreadcrumbItem } from '@/components/PageBreadcrumbs.vue'
import type { AgencyById } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types.ts'

import { buildBreadcrumb } from './buildBreadcrumb'

export const useReviewsAgencyBreadcrumbs = (agency: Pick<AgencyById, 'id' | 'title'>, leadName?: Ref<string>): Ref<BreadcrumbItem[]> => {
  const route = useRoute()
  const { t } = useI18n()

  const params = { agencyId: agency.id }
  const agencyName = agency.title

  return computed(() => {
    const root = buildBreadcrumb(route, { name: ReviewsRouteName.ReviewsAgencies }, t('breadcrumbs.reviews.agencies'))

    const reviewsAgencyViewDetails = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyViewDetails, params },
      t('breadcrumbs.reviews.agency.view.details', { agencyName }),
    )

    const reviewsAgencyEditDetails = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyEditDetails, params },
      t('breadcrumbs.reviews.agency.edit.details', { agencyName }),
    )

    const reviewsAgencyViewIndustriesAndServices = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyViewIndustriesAndServices, params },
      t('breadcrumbs.reviews.agency.view.industriesAndServices', { agencyName }),
    )

    const reviewsAgencyEditIndustriesAndServices = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyEditIndustriesAndServices, params },
      t('breadcrumbs.reviews.agency.edit.industriesAndServices', { agencyName }),
    )

    const reviewsAgencyViewTraits = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyViewTraits, params },
      t('breadcrumbs.reviews.agency.view.traits', { agencyName }),
    )

    const reviewsAgencyEditTraits = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyEditTraits, params },
      t('breadcrumbs.reviews.agency.edit.traits', { agencyName }),
    )

    const reviewsAgencyViewPartners = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyViewPartners, params },
      t('breadcrumbs.reviews.agency.view.partners', { agencyName }),
    )

    const reviewsAgencyEditPartners = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyEditPartners, params },
      t('breadcrumbs.reviews.agency.edit.partners', { agencyName }),
    )

    const reviewsProductViewAssets = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyViewAssets, params },
      t('breadcrumbs.reviews.agency.view.assets', { agencyName }),
    )

    const reviewsProductEditAssets = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyEditAssets, params },
      t('breadcrumbs.reviews.agency.edit.assets', { agencyName }),
    )
    const reviewsAgencyResourcesView = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyResourcesView, params },
      t('breadcrumbs.reviews.agency.view.resources', { agencyName }),
    )

    const reviewsAgencyResourcesWidgetsView = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyResourcesWidgetsView, params },
      t('breadcrumbs.reviews.agency.view.resources', { agencyName }),
    )

    const reviewsAgencyResourcesBadgesView = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyResourcesBadgesView, params },
      t('breadcrumbs.reviews.agency.view.resources', { agencyName }),
    )

    const reviewsAgencyReviewsView = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyReviewsView, params },
      t('breadcrumbs.reviews.agency.view.reviews', { agencyName }),
    )

    const reviewsAgencyPreferencesView = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyPreferencesView, params },
      t('breadcrumbs.reviews.agency.view.preferences', { agencyName }),
    )

    const reviewsAgencyPreferencesEdit = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyPreferencesEdit, params },
      t('breadcrumbs.reviews.agency.edit.preferences', { agencyName }),
    )

    const reviewsAgencyViewLeads = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyLeadsView, params },
      t('breadcrumbs.reviews.agency.view.leads', { agencyName }),
    )

    const reviewsAgencyLeadDetail = buildBreadcrumb(
      route,
      { name: ReviewsRouteName.ReviewsAgencyLeadDetail, params },
      leadName?.value || '',
    )

    const breadcrumbsMap: Partial<Record<ReviewsRouteName, BreadcrumbItem[]>> = {
      [ReviewsRouteName.ReviewsAgencyView]: [],
      [ReviewsRouteName.ReviewsAgencyEdit]: [],
      [ReviewsRouteName.ReviewsAgenciesIndex]: [root],
      [ReviewsRouteName.ReviewsAgencyViewDetails]: [root, reviewsAgencyViewDetails],
      [ReviewsRouteName.ReviewsAgencyEditDetails]: [root, reviewsAgencyViewDetails, reviewsAgencyEditDetails],
      [ReviewsRouteName.ReviewsAgencyViewIndustriesAndServices]: [root, reviewsAgencyViewIndustriesAndServices],
      [ReviewsRouteName.ReviewsAgencyEditIndustriesAndServices]: [
        root,
        reviewsAgencyViewIndustriesAndServices,
        reviewsAgencyEditIndustriesAndServices,
      ],
      [ReviewsRouteName.ReviewsAgencyViewTraits]: [root, reviewsAgencyViewTraits],
      [ReviewsRouteName.ReviewsAgencyEditTraits]: [root, reviewsAgencyViewTraits, reviewsAgencyEditTraits],
      [ReviewsRouteName.ReviewsAgencyViewPartners]: [root, reviewsAgencyViewPartners],
      [ReviewsRouteName.ReviewsAgencyEditPartners]: [root, reviewsAgencyViewPartners, reviewsAgencyEditPartners],
      [ReviewsRouteName.ReviewsAgencyViewAssets]: [root, reviewsProductViewAssets],
      [ReviewsRouteName.ReviewsAgencyEditAssets]: [root, reviewsProductViewAssets, reviewsProductEditAssets],
      [ReviewsRouteName.ReviewsAgencyResourcesView]: [root, reviewsAgencyResourcesView],
      [ReviewsRouteName.ReviewsAgencyResourcesWidgetsView]: [root, reviewsAgencyResourcesWidgetsView],
      [ReviewsRouteName.ReviewsAgencyResourcesBadgesView]: [root, reviewsAgencyResourcesBadgesView],
      [ReviewsRouteName.ReviewsAgencyReviewsView]: [root, reviewsAgencyReviewsView],
      [ReviewsRouteName.ReviewsAgencyLeadsView]: [root, reviewsAgencyViewLeads],
      [ReviewsRouteName.ReviewsAgencyLeadDetail]: [root, reviewsAgencyViewLeads, reviewsAgencyLeadDetail],
      [ReviewsRouteName.ReviewsAgencyPreferencesView]: [root, reviewsAgencyPreferencesView],
      [ReviewsRouteName.ReviewsAgencyPreferencesEdit]: [root, reviewsAgencyPreferencesView, reviewsAgencyPreferencesEdit],
    }

    return breadcrumbsMap[route.name as ReviewsRouteName] || []
  })
}
